"use client"

import type React from "react"

import { useEffect, useRef } from "react"
import { I18nextProvider } from "react-i18next"
import {
  DEFAULT_LANGUAGE,
  normalizeLanguage,
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
} from "@/lib/language"
import {
  createI18nInstance,
} from "@/lib/i18n"

const SUPPORTED_LANGUAGE_SET = new Set<SupportedLanguage>(SUPPORTED_LANGUAGES)
const LANGUAGE_STORAGE_KEY = "i18nextLng"
const LANGUAGE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365

function persistLanguage(language: SupportedLanguage) {
  window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language)
  document.cookie = `${LANGUAGE_STORAGE_KEY}=${language}; path=/; max-age=${LANGUAGE_COOKIE_MAX_AGE}; SameSite=Lax`
}

export function I18nProvider({
  children,
  initialLanguage = DEFAULT_LANGUAGE,
}: {
  children: React.ReactNode
  initialLanguage?: SupportedLanguage
}) {
  const i18nRef = useRef(createI18nInstance(initialLanguage))
  const i18n = i18nRef.current

  useEffect(() => {
    const resolvePreferredLanguage = () => {
      const searchParams = new URLSearchParams(window.location.search)
      const queryLanguage = normalizeLanguage(searchParams.get("lang"))
      if (queryLanguage && SUPPORTED_LANGUAGE_SET.has(queryLanguage)) {
        return queryLanguage
      }

      const storedLanguage = normalizeLanguage(window.localStorage.getItem(LANGUAGE_STORAGE_KEY))
      if (storedLanguage && SUPPORTED_LANGUAGE_SET.has(storedLanguage)) {
        return storedLanguage
      }

      const browserLanguage = normalizeLanguage(window.navigator.language)
      if (browserLanguage && SUPPORTED_LANGUAGE_SET.has(browserLanguage)) {
        return browserLanguage
      }

      return initialLanguage
    }

    // Update document attributes when language changes
    const updateDocumentAttributes = (language: SupportedLanguage) => {
      const currentLang = language
      document.documentElement.lang = currentLang
      document.documentElement.dir = currentLang === "ar" ? "rtl" : "ltr"
      document.documentElement.style.direction = currentLang === "ar" ? "rtl" : "ltr"
      persistLanguage(currentLang)

      // Update font class
      if (currentLang === "ar") {
        document.body.classList.remove("font-inter")
        document.body.classList.add("font-arabic")
      } else {
        document.body.classList.remove("font-arabic")
        document.body.classList.add("font-inter")
      }
    }

    // Listen for language changes
    const handleLanguageChanged = (language: string) => {
      const normalizedLanguage = normalizeLanguage(language) ?? initialLanguage
      updateDocumentAttributes(normalizedLanguage)
    }

    i18n.on("languageChanged", handleLanguageChanged)

    const activeLanguage = normalizeLanguage(i18n.resolvedLanguage || i18n.language) ?? initialLanguage
    updateDocumentAttributes(activeLanguage)

    const preferredLanguage = resolvePreferredLanguage()

    if (preferredLanguage !== activeLanguage) {
      void i18n.changeLanguage(preferredLanguage)
    }

    // Expose i18n to the window object for debugging/testing
    if (typeof window !== "undefined") {
      ;(window as any).i18n = i18n
    }

    return () => {
      i18n.off("languageChanged", handleLanguageChanged)
    }
  }, [i18n, initialLanguage])

  return (
    <I18nextProvider i18n={i18n}>
      {children}
    </I18nextProvider>
  )
}
